import { useRef } from "react";
import { Autocomplete as MuiAutocomplete, Box, Chip, TextField } from "@mzc-pdc/ui";
import { IconCancelCircle, IconErrorCircle } from "@mzc-cloudplex/icons";
import EllipsisTooltip from "@components_v2/ellipsis-tooltip/ellipsis-tooltip";
import theme from "@styles/theme";
import i18n from "@lib/i18n";

// 추천 목록이 끝에서 이만큼 남았을 때 다음 페이지를 요청한다
const FETCH_NEXT_THRESHOLD_PX = 40;

/**
 * @param {object} props
 * @param {string[]} [props.suggestions] 입력값으로 조회한 추천 태그. 비우면 추천 없이 자유 입력만 한다(기존 동작)
 * @param {() => void} [props.onFetchNextSuggestions] 추천 목록 끝에 닿았을 때 다음 페이지 요청
 * @param {(tag: string) => void} [props.onSelectSuggestion] 추천 항목 선택(클릭·방향키+Enter)
 */
export const AutocompleteTags = ({
    type,
    open,
    tags,
    label,
    value,
    disabled,
    inputDisabled,
    error,
    setValue,
    onDelete,
    onSubmit,
    onScroll,
    onClickOption,
    isEdit,
    placeholder,
    helperText,
    popperComponent,
    noOptionsText,
    maxCount,
    suggestions = [],
    onFetchNextSuggestions,
    onSelectSuggestion,
}) => {
    // 중복 검증(uniqueInTrimmed)과 같은 trim 기준으로 입력값과 일치하는 칩을 하이라이트한다
    const isMatchedWithInput = (option) => !!value.trim() && String(option ?? "").trim() === value.trim();

    // 방향키+Enter 로 추천을 고르면 MUI 가 keydown 에서 선택을 처리한다. 뒤따르는 keyup Enter 가
    // 입력 중이던 글자를 태그로 한 번 더 추가하지 않도록 이번 Enter 는 건너뛴다.
    const selectedOnKeyDownRef = useRef(false);

    return (
        // Note: adaptive-popper를 적용하기 위해서 box를 추가
        <MuiAutocomplete
            clearText={i18n.t(`common::label::Clear`, `Clear`)}
            openText={i18n.t(`common::label::Open`, `Open`)}
            closeText={i18n.t(`common::label::Close`, `Close`)}
            freeSolo={true} //Note : freesolo를 사용시 옵션과는 상관없는 값을 사용할 수 있게 되서 noOptionsText를 사용불가능(옵션 사용하는 경우 false 사용해야함)
            multiple
            open={open}
            value={tags ? tags.map((tag) => tag.value) : []}
            options={suggestions}
            // 추천은 서버가 입력값으로 이미 걸러 준 결과다 — 클라이언트에서 다시 거르지 않는다
            filterOptions={(options) => options}
            onChange={(event, newValue, reason, details) => {
                // 칩 추가·삭제는 onSubmit·onDelete 가 직접 관리한다. 여기서는 추천 선택만 받는다.
                if (reason !== "selectOption" || !onSelectSuggestion) return;
                if (event?.type === "keydown") selectedOnKeyDownRef.current = true;
                onSelectSuggestion(details.option);
            }}
            ListboxProps={{
                onScroll: (event) => {
                    if (!onFetchNextSuggestions) return;
                    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
                    if (scrollTop + clientHeight >= scrollHeight - FETCH_NEXT_THRESHOLD_PX) onFetchNextSuggestions();
                },
            }}
            renderOption={(props, option) => (
                <li {...props} key={option}>
                    <EllipsisTooltip
                        text={`# ${option}`}
                        variant="body2"
                        // 옵션 li는 flex라 너비 제한이 없으면 텍스트 길이만큼 늘어나 말줄임·툴팁 없이 잘린다
                        sx={{ minWidth: 0 }}
                        typographyProps={{ sx: { whiteSpace: "pre" } }}
                    />
                </li>
            )}
            PopperComponent={popperComponent ?? null}
            disableCloseOnSelect={!!tags}
            size={`small`}
            disabled={disabled}
            getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                else return option?.title;
            }}
            noOptionsText={noOptionsText} //Note : Chip 개선에 들어가는 검색 결과에 따른 옵션이 없을 경우 안내가 들어감
            renderTags={(v, getTagProps) => {
                return v.map((option, index) => (
                    <Chip
                        key={option}
                        {...getTagProps({ index })}
                        size={`small`}
                        label={
                            <EllipsisTooltip
                                text={`# ${option}`}
                                maxWidth={200}
                                variant="body3"
                                // 태그 값의 연속 공백이 접혀 보이면 값이 변형된 것처럼 읽힌다 — 원문 그대로 표시한다
                                typographyProps={{ sx: { whiteSpace: "pre" } }}
                            />
                        }
                        avatar={<></>}
                        color={isMatchedWithInput(option) || (value && index === maxCount - 1) ? "error" : undefined}
                        disabled={disabled}
                        onDelete={() => onDelete(index)}
                        deleteIcon={<IconCancelCircle size={12} />}
                        sx={{
                            marginRight: "4px",
                        }}
                    />
                ));
            }}
            renderInput={(params) => {
                return (
                    <>
                        <TextField
                            {...params}
                            inputProps={{ ...params.inputProps, value: value, readOnly: inputDisabled }}
                            {...(label
                                ? {
                                      label,
                                      InputLabelProps: {
                                          shrink: true,
                                          sx: { backgroundColor: theme.palette.background.paper, px: "4px" },
                                      },
                                  }
                                : { hiddenLabel: true })}
                            size={"small"}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        {params.InputProps.startAdornment}
                                        <Box sx={{ margin: `0 0 0 5px`, color: `#9898ad` }}>#</Box>
                                    </>
                                ),
                                endAdornment: error ? <IconErrorCircle color={`error`} size={16} /> : undefined,
                            }}
                            placeholder={tags?.length > 0 ? "" : placeholder}
                            error={error}
                            helperText={helperText}
                            onChange={(e) => {
                                setValue(e.target.value);
                            }}
                            onKeyDown={(e) => {
                                if (tags?.length > 0 && e.code === "Backspace" && value === "") {
                                    onDelete(tags.length - 1);
                                }
                            }}
                            onKeyUp={(e) => {
                                if (e.code !== "Enter" && e.code !== "NumpadEnter") return;
                                if (selectedOnKeyDownRef.current) {
                                    selectedOnKeyDownRef.current = false;
                                    return;
                                }
                                if (!error) onSubmit();
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: `#fff`,
                                    borderRadius: "2px",
                                    "&:hover": {
                                        "& > fieldset": { borderColor: "#2763ea" },
                                    },
                                },
                                "&.Mui-focused": {
                                    ".MuiOutlinedInput-notchedOutline": {
                                        borderWidth: `1px`,
                                    },
                                },
                                ".MuiInputBase-input": {
                                    border: `none`,
                                    "&::placeholder": {
                                        color: "#9898ad",
                                        weight: "normal",
                                        // opacity: 1,
                                    },
                                },
                                ".MuiFormHelperText-root": {
                                    marginLeft: 0,
                                    marginTop: 1,
                                    fontSize: `14px`,
                                },
                                ...(isEdit && {
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: `#fff`,
                                        borderRadius: "2px",
                                        "> fieldset": { borderColor: "#2763ea" },
                                    },
                                }),
                                ...(disabled && {
                                    "& .MuiOutlinedInput-root .Mui-disabled": {
                                        backgroundColor: `#fff`,
                                    },
                                }),
                            }}
                        />
                    </>
                );
            }}
            sx={{
                "&.MuiAutocomplete-root .MuiInputBase-root": {
                    minHeight: `40px`,
                    backgroundColor: theme.palette.background.default,
                },
            }}
            // open={false} //option dropdown 비활성화
        />
    );
};
